"use client"

import {
  ArrowPathIcon,
  ArrowRightStartOnRectangleIcon,
} from "@heroicons/react/16/solid"
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"
import Button from "@src/components/Button"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { NearIntentsLogoSymbolIcon } from "@src/icons"
import {
  useBootstrapActor,
  useBootstrapSelector,
  useBootstrapState,
} from "@src/providers/AppBootstrapProvider"
import type { ChainType } from "@src/types/chainType"
import { logger } from "@src/utils/logger"
import type { ReactNode } from "react"

export default function SplashScreen({ children }: { children: ReactNode }) {
  const machineState = useBootstrapState()

  if (machineState === "initializing") {
    return <SplashScreenContent />
  }

  if (machineState === "session_unreachable") {
    return <SessionUnreachableContent />
  }

  return children
}

export function SplashScreenContent() {
  return (
    <div
      data-testid="splash-screen"
      className="fade-in relative flex h-screen w-screen animate-in flex-col items-center justify-center gap-6 overflow-hidden bg-gray-800 duration-300 ease-out"
    >
      <h1 className="sr-only">Loading application</h1>
      <NearIntentsLogoSymbolIcon className="size-18 shrink-0" />
      <ThreeDotsLoader />
    </div>
  )
}

export function SessionUnreachableContent() {
  const bootstrapActor = useBootstrapActor()
  const { signOut } = useConnectWallet()
  const failureReason = useBootstrapSelector((snap) => {
    const result = snap.context.sessionResult
    return result?.status === "retryable_failure" ? result.reason : null
  })
  const chainType = useBootstrapSelector((snap) => snap.context.chainType)

  return (
    <div className="fade-in dark relative flex h-screen w-screen animate-in flex-col items-center justify-center gap-6 overflow-hidden bg-gray-800 px-6 duration-300 ease-out">
      <div className="flex size-14 items-center justify-center rounded-full bg-yellow-500/10">
        <ExclamationTriangleIcon className="size-6 text-yellow-300" />
      </div>
      <div className="max-w-sm text-center">
        <h1 className="font-semibold text-2xl/8 text-white tracking-tight">
          Couldn’t verify your session
        </h1>
        <p className="mt-2 text-balance font-medium text-base/6 text-gray-400">
          {failureReason ??
            "We couldn't reach the authentication service. Check your connection and try again."}
        </p>
      </div>
      <div className="flex w-full max-w-sm flex-col gap-2">
        <Button
          size="xl"
          fullWidth
          onClick={() => bootstrapActor.send({ type: "RETRY_SESSION" })}
          data-testid="session-unreachable-retry"
        >
          Try again
          <ArrowPathIcon className="size-4 shrink-0" />
        </Button>
        <Button
          size="xl"
          fullWidth
          variant="secondary"
          onClick={async () => {
            try {
              if (chainType) {
                bootstrapActor.send({ type: "SIGN_OUT" })
                await signOut({ id: chainType as ChainType })
              }
            } catch (err) {
              logger.error(err)
            }
          }}
          data-testid="session-unreachable-signout"
        >
          Sign out
          <ArrowRightStartOnRectangleIcon className="size-4 shrink-0" />
        </Button>
      </div>
    </div>
  )
}

const ThreeDotsLoader = () => (
  <div data-testid="splash-loader" className="flex gap-1.5">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="size-1.5 animate-dot-bounce rounded-full bg-white/60"
        style={{ animationDelay: `${i * 160}ms` }}
      />
    ))}
  </div>
)
